import { lazy, Suspense, useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { getErrorMessage } from '@/lib/platform';

import { ridesApi } from '@/lib/rides-api';
import { getContactPhone } from '@/lib/contact';
import { useRideLive } from '@/lib/use-ride-booking';
import { formatMoney } from '@/lib/format';
import {
  RIDE_CANCEL_REASONS,
  rideStatusText,
  rideStatusVariant,
  visibleRideState,
} from '@/lib/ride-format';
import { RideStateTimeline } from '@/components/rides/ride-state-timeline';
import { OtpCard } from '@/components/rides/otp-card';
import { DriverVehicleCard } from '@/components/rides/driver-vehicle-card';
import { RideFareCard } from '@/components/rides/ride-fare-card';
import { RateRideModal } from '@/components/rides/rate-ride-modal';
import { TipModal } from '@/components/rides/tip-modal';
import { ReportProblemModal, type ReportCategory } from '@/components/shared/report-problem-modal';
import { Badge } from '@/components/shared/badge';
import { ErrorState } from '@/components/shared/error-state';
import { Skeleton } from '@/components/shared/skeleton';
import { Screen } from '@/components/chrome';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

// Buyer-raisable TRV10 ride IGM grievances (the FLM/ORD/PMT 111-series from the ONDC
// "Mobility – IGM Category and Sub Category" TRV10 sheet). The backend derives the parent
// category from the code; safety issues (rude driver, accident) go through ride rating, not IGM.
// See @.claude/rules/ondc-trv10-enums-tags.md.
const RIDE_ISSUE_CATEGORIES: ReportCategory[] = [
  { code: 'FLM111', label: "Driver couldn't end the trip" },
  { code: 'FLM112', label: 'Driver took a longer/different route' },
  { code: 'FLM113', label: 'Trip OTP not showing in the app' },
  { code: 'FLM114', label: "Trip OTP didn't work with the driver" },
  { code: 'FLM115', label: 'Vehicle broke down during the trip' },
  { code: 'ORD111', label: 'I left an item in the vehicle' },
  { code: 'PMT111', label: 'Driver demanded extra fare' },
  { code: 'PMT112', label: 'Fare changed between start and end' },
  { code: 'PMT113', label: 'Charged extra by mistake' },
  { code: 'PMT114', label: 'Charged for airport/station pickup' },
  { code: 'PMT117', label: 'Asked to pay toll already in the fare' },
  { code: 'PMT118', label: 'Driver avoided the paid toll road' },
  { code: 'PMT119', label: 'Refund/payment delayed' },
];

// Lazy-load the live map — mapbox-gl is ~550KB and only needed on this screen.
const RideLiveMap = lazy(() =>
  import('@/components/rides/ride-live-map').then((m) => ({ default: m.RideLiveMap }))
);

const DEFAULT_CANCEL_REASON = RIDE_CANCEL_REASONS[0]?.code ?? '005';

export const Route = createFileRoute('/_authed/rides/$id')({
  component: RideDetailPage,
});

type CancelStage = 'none' | 'choose' | 'soft' | 'preview' | 'confirm' | 'done';

function RideDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();

  const [cancelStage, setCancelStage] = useState<CancelStage>('none');
  const [cancelReason, setCancelReason] = useState(DEFAULT_CANCEL_REASON);
  const [cancelError, setCancelError] = useState('');
  const [rateOpen, setRateOpen] = useState(false);
  const [ratePrompted, setRatePrompted] = useState(false);
  const [rated, setRated] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipped, setTipped] = useState('');
  // Add-stop (TRV10 2.1.0 multi-stop, mode ADD): a single intermediate stop by GPS.
  const [stopOpen, setStopOpen] = useState(false);
  const [stopGps, setStopGps] = useState('');
  const [stopError, setStopError] = useState('');

  const awaitingCancel = cancelStage === 'soft' || cancelStage === 'confirm';
  // Poll faster (1.5s) while the cancel handshake is in flight.
  const { ride, tracking, active, isLoading, isError, error, refetch } = useRideLive(id, awaitingCancel);

  const cancelMut = useMutation({
    mutationFn: (vars: { stage: 'SOFT' | 'CONFIRM'; reason?: string }) => ridesApi.cancel(id, vars),
  });
  const rateMut = useMutation({
    mutationFn: (value: string) => ridesApi.rate(id, { category: 'DRIVER', value }),
  });
  const tipMut = useMutation({
    mutationFn: (amount: string) => ridesApi.tip(id, { amount }),
  });
  // mode ADD is single-shot (no SOFT→CONFIRM); the END-stop CHANGE handshake is
  // supported by ridesApi.stops but not surfaced here (rare mid-trip action).
  // `stage` is sent explicitly only to satisfy the generated type: the spec gives
  // both `mode` and `stage` a `default`, and openapi-typescript marks defaulted
  // properties as always-present. These are the server's own defaults, so the wire
  // payload is unchanged.
  const stopMut = useMutation({
    mutationFn: (gps: string) =>
      ridesApi.stops(id, {
        mode: 'ADD',
        stage: 'SOFT',
        stops: [{ type: 'INTERMEDIATE_STOP', gps, address: 'Intermediate stop' }],
      }),
  });

  const gpsValid = /^-?\d{1,3}(\.\d+)?,\s*-?\d{1,3}(\.\d+)?$/.test(stopGps.trim());

  async function addStop() {
    if (!gpsValid) return;
    setStopError('');
    try {
      await stopMut.mutateAsync(stopGps.trim());
      setStopOpen(false);
      setStopGps('');
      void refetch();
    } catch (e) {
      setStopError(getErrorMessage(e));
    }
  }

  // Advance the 2-stage cancel as the order status lands via polling. The terminal
  // reconcile (cancelled → done from any mid-cancel stage) covers a CONFIRM that the
  // 35s timeout bounced back to 'preview' but which later landed — without it the
  // screen could show "Cancelled" alongside a live "Confirm cancellation" button.
  useEffect(() => {
    if (!ride) return;
    const isCancelled = ride.status === 'CANCELLED' || ride.rideState === 'RIDE_CANCELLED';
    if (isCancelled) {
      if (cancelStage !== 'none' && cancelStage !== 'done') setCancelStage('done');
      return;
    }
    // SOFT preview keys on status alone — the refund/charges block is optional and
    // renders only when the BPP includes a cancellation object.
    if (cancelStage === 'soft' && ride.status === 'SOFT_CANCEL') {
      setCancelStage('preview');
    }
  }, [ride, cancelStage]);

  // Don't spin forever if the operator's cancel callback never lands (Beckn TTL PT30S).
  useEffect(() => {
    if (cancelStage !== 'soft' && cancelStage !== 'confirm') return;
    const timer = setTimeout(() => {
      setCancelError(
        cancelStage === 'soft'
          ? 'The operator is taking a while to respond. Please try again.'
          : 'Cancellation is taking longer than expected. Check back shortly, or try again.'
      );
      setCancelStage((s) => (s === 'soft' ? 'none' : 'preview'));
    }, 35_000);
    return () => clearTimeout(timer);
  }, [cancelStage]);

  const visibleState = visibleRideState(ride?.rideState, ride?.statusReady);
  const ended = visibleState === 'RIDE_ENDED';

  // Prompt for a rating once, automatically, when the ride completes.
  useEffect(() => {
    if (ended && !rated && !ratePrompted) {
      setRateOpen(true);
      setRatePrompted(true);
    }
  }, [ended, rated, ratePrompted]);

  function startCancel() {
    setCancelError('');
    setCancelStage('soft');
    cancelMut.mutate(
      { stage: 'SOFT', reason: cancelReason },
      {
        onError: (e) => {
          setCancelError(cancelErrorMessage(e));
          setCancelStage('none');
        },
      }
    );
  }
  function confirmCancel() {
    setCancelStage('confirm');
    cancelMut.mutate(
      { stage: 'CONFIRM', reason: cancelReason },
      {
        onError: (e) => {
          setCancelError(cancelErrorMessage(e));
          setCancelStage('preview');
        },
      }
    );
  }

  const back = () => void navigate({ to: '/trips' });

  if (isLoading) {
    return (
      <Screen title="Your ride" onBack={back} accent="ride">
        <div className="wb-col wb-gap-16">
          <Skeleton w="50%" h={24} />
          <Skeleton w="100%" h={280} r={22} />
          <Skeleton w="100%" h={160} r={22} />
        </div>
      </Screen>
    );
  }
  if (isError || !ride) {
    return (
      <Screen title="Your ride" onBack={back} accent="ride">
        <ErrorState error={error} onRetry={() => void refetch()} />
      </Screen>
    );
  }

  const fare = ride.quote?.total ?? ride.fare;
  const cancelled = ride.rideState === 'RIDE_CANCELLED' || ride.status === 'CANCELLED';
  const cancellable = ride.cancellable && !ended && !cancelled;

  return (
    <Screen title="Your ride" onBack={back} accent="ride">
      <div className="wb-col wb-gap-16">
        <div className="wb-row wb-between wb-gap-8" style={{ alignItems: 'flex-start' }}>
          <div className="wb-col wb-gap-4">
            <span className="wb-eyebrow wb-row wb-gap-6">
              {active && !cancelled && <span className="wb-livedot" />} Ride
            </span>
            <h1 className="wb-h2">Your ride</h1>
          </div>
          <Badge variant={rideStatusVariant(visibleState, ride.status)}>
            {rideStatusText(visibleState, ride.status)}
          </Badge>
        </div>

        <Suspense fallback={<Skeleton w="100%" h={280} r={22} />}>
          <RideLiveMap
            pickupGps={ride.pickup?.gps}
            dropGps={ride.drop?.gps}
            driverGps={ride.driverGps ?? tracking?.gps}
            encodedPolyline={ride.route?.encodedPolyline}
          />
        </Suspense>

        <RideStateTimeline rideState={visibleState} />

        {ride.authorization && (
          <OtpCard authorization={ride.authorization} rideState={visibleState} />
        )}

        {!cancelled && <DriverVehicleCard driver={ride.driver} vehicle={ride.vehicle} />}

        <RideFareCard
          quote={ride.quote}
          fare={ride.fare}
          paymentNote={
            cancelled
              ? undefined
              : ended
                ? 'Pay the driver directly.'
                : `You'll pay the driver ${formatMoney(fare)} at the end of the ride.`
          }
        />

        {/* Cancellation preview / outcome */}
        {ride.cancellation && cancelStage !== 'soft' && (
          <div
            className="wb-card wb-card--pad wb-col wb-gap-4"
            style={{ background: 'var(--honey-wash-2)' }}
          >
            <p className="wb-strong">{cancelled ? 'Cancelled' : 'Cancellation preview'}</p>
            {ride.cancellation.refund && (
              <p className="wb-small">Refund: {formatMoney(ride.cancellation.refund)}</p>
            )}
            {ride.cancellation.charges && (
              <p className="wb-small">Charges: {formatMoney(ride.cancellation.charges)}</p>
            )}
          </div>
        )}

        {cancelError && (
          <p className="wb-small" style={{ color: 'var(--danger)' }}>
            {cancelError}
          </p>
        )}

        {/* Cancel reason chooser */}
        {cancelStage === 'choose' && (
          <div className="wb-card wb-card--pad wb-col wb-gap-8">
            <p className="wb-strong">Why are you cancelling?</p>
            {RIDE_CANCEL_REASONS.map((r) => (
              <label
                key={r.code}
                className={`wb-option ${cancelReason === r.code ? 'is-selected' : ''}`}
                style={{ cursor: 'pointer' }}
              >
                <span className="wb-radio-dot" />
                <input
                  type="radio"
                  name="cancel-reason"
                  checked={cancelReason === r.code}
                  onChange={() => setCancelReason(r.code)}
                  style={{ position: 'absolute', opacity: 0 }}
                />
                <span className="wb-grow">{r.label}</span>
              </label>
            ))}
            <div className="wb-row wb-gap-8" style={{ paddingTop: 4 }}>
              <Button variant="secondary" size="sm" onClick={() => setCancelStage('none')}>
                Keep ride
              </Button>
              <Button variant="danger" size="sm" onClick={startCancel}>
                Cancel ride
              </Button>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="wb-row wb-wrap wb-gap-8">
          <Button variant="secondary" size="sm" onClick={() => setReportOpen(true)}>
            Report a problem
          </Button>

          {cancellable && cancelStage === 'none' && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                setCancelError('');
                setCancelStage('choose');
              }}
            >
              Cancel ride
            </Button>
          )}
          {awaitingCancel && (
            <span className="wb-row wb-gap-8 wb-small">
              <Spinner />
              {cancelStage === 'soft' ? 'Checking cancellation…' : 'Cancelling…'}
            </span>
          )}
          {cancelStage === 'preview' && !cancelled && (
            <Button variant="danger" size="sm" onClick={confirmCancel}>
              Confirm cancellation
            </Button>
          )}
          {ended && !rated && (
            <Button variant="primary" size="sm" onClick={() => setRateOpen(true)}>
              Rate your ride
            </Button>
          )}
          {rated && <span className="wb-small">Thanks for rating!</span>}

          {/* Add an intermediate stop while the ride is in progress (TRV10 2.1.0). */}
          {active && !cancelled && !ended && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setStopError('');
                setStopOpen((o) => !o);
              }}
            >
              Add a stop
            </Button>
          )}

          {/* Tip the driver during or after the ride (not once cancelled). */}
          {!cancelled && !tipped && (
            <Button variant="secondary" size="sm" onClick={() => setTipOpen(true)}>
              Tip driver
            </Button>
          )}
          {tipped && <span className="wb-small">Tip of ₹{tipped} added.</span>}
        </div>

        {/* Add-stop panel (mode ADD) */}
        {stopOpen && (
          <div className="wb-card wb-card--pad wb-col wb-gap-8">
            <p className="wb-strong">Add an intermediate stop</p>
            <input
              type="text"
              value={stopGps}
              onChange={(e) => setStopGps(e.target.value)}
              placeholder="Stop GPS — e.g. 12.9716, 77.5946"
              className="wb-field"
            />
            {stopError && (
              <p className="wb-small" style={{ color: 'var(--danger)' }}>
                {stopError}
              </p>
            )}
            <div className="wb-row wb-gap-8" style={{ justifyContent: 'flex-end', paddingTop: 4 }}>
              <Button variant="ghost" size="sm" onClick={() => setStopOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={addStop}
                disabled={!gpsValid || stopMut.isPending}
              >
                {stopMut.isPending ? 'Adding…' : 'Add stop'}
              </Button>
            </div>
          </div>
        )}

        {rateOpen && (
          <RateRideModal
            onClose={() => setRateOpen(false)}
            onSubmit={async (value) => {
              await rateMut.mutateAsync(value);
              setRated(true);
              setRateOpen(false);
            }}
          />
        )}
        {tipOpen && (
          <TipModal
            onClose={() => setTipOpen(false)}
            onSubmit={async (amount) => {
              await tipMut.mutateAsync(amount);
              setTipped(amount);
              setTipOpen(false);
              void refetch();
            }}
          />
        )}
        {reportOpen && (
          <ReportProblemModal
            placeholder="Describe the issue with your ride…"
            onClose={() => setReportOpen(false)}
            categories={RIDE_ISSUE_CATEGORIES}
            uploader={ridesApi.uploadIssueAttachment}
            onSubmit={async (description, extras) => {
              const ack = await ridesApi.raiseIssue(id, {
                igmVersion: '1.0.0',
                description,
                subCategory: extras.subCategory,
                contactPhone: getContactPhone(),
                images: extras.images,
              });
              setReportOpen(false);
              void navigate({
                to: '/support/$category/$issueId',
                params: { category: 'rides', issueId: ack.issueId },
              });
            }}
          />
        )}
      </div>
    </Screen>
  );
}

// ONDC_012/ONDC_023 are state-transition failures that mean the ride is already
// mid-cancel / ended for this stage.
function cancelErrorMessage(e: unknown): string {
  const err = e as { status?: number; code?: string } | null;
  if (err?.status === 409 || err?.code === 'ONDC_012' || err?.code === 'ONDC_023') {
    return "This ride can't be cancelled right now — it may already be cancelling or ended. Pull to refresh for the latest.";
  }
  return getErrorMessage(e);
}
